// Services/EmployeeServices/LeaveRequestService.js
import { useState } from "react";
import { message } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

export default function LeaveRequestService() {
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch employee's leave balance
  const fetchLeaveBalance = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API.leavePolicy.myBalance);
      setLeavePolicies(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching leave balance:", error);
      message.error("Failed to load leave policies");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Submit leave request - ENHANCED ERROR HANDLING WITH BETTER TOASTS
  const submitLeaveRequest = async (payload) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(API.leaveRequest.createLeaveRequest, payload, {
        timeout: 15000,
        headers: {
          "Content-Type": "application/json",
        },
      });


      if (response.data) {
        message.success({
          content: "Leave request submitted successfully!",
          duration: 4,
          style: {
            marginTop: "50px",
          },
        });
        return response.data;
      }
    } catch (error) {
      console.error("❌ FULL ERROR OBJECT:", error);

      if (error.response) {
        const responseData = error.response.data;
        const status = error.response.status;

        console.error("📊 Error Status:", status);
        console.error("📊 Error Data:", responseData);

        //  Get the specific error message
        const errorMessage = responseData.details || "Failed to submit leave request";

        //  Show ALL 500 errors with details as warnings (they're business logic errors)
        if (status === 500 && responseData.details) {
          message.warning(errorMessage, 6);
        }
        //  Show other client errors as warnings
        else if (status >= 400 && status < 500) {
          message.warning(errorMessage, 6);
        }
        //  Only show generic error for real server issues
        else {
          message.error("Server error. Please try again later.", 5);
        }
      } else if (error.request) {
        message.error("No response from server. Please check your internet connection.");
      } else {
        message.error(`Request failed: ${error.message}`);
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Enhanced function to handle specific leave conflict scenarios
  const handleLeaveConflict = (serverError) => {
    const errorMessage = serverError?.message || "";

    // Common leave conflict patterns
    if (errorMessage.includes("already taken") || errorMessage.includes("already requested")) {
      return {
        message:
          "You have already taken or requested leave on these dates. Please choose different dates.",
        type: "warning",
        duration: 6,
      };
    }

    if (errorMessage.includes("overlap") || errorMessage.includes("conflict")) {
      return {
        message: "These dates overlap with an existing leave request. Please adjust your dates.",
        type: "warning",
        duration: 6,
      };
    }

    if (errorMessage.includes("insufficient") || errorMessage.includes("available days")) {
      return {
        message: "Insufficient available days for this leave type. Please check your balance.",
        type: "warning",
        duration: 5,
      };
    }

    if (errorMessage.includes("weekend") || errorMessage.includes("holiday")) {
      return {
        message: "Leave cannot be applied on weekends or holidays. Please select working days.",
        type: "info",
        duration: 5,
      };
    }

    return null;
  };

  // Check if policy has available days
  const hasAvailableDays = (policy) => {
    return policy.availableDays > 0;
  };

  // Calculate number of days between dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Get user-friendly error message
  const getErrorMessage = (error) => {
    if (error.response?.data) {
      const serverError = error.response.data;

      // Try to extract meaningful message
      if (typeof serverError === "string") return serverError;
      if (serverError.message) return serverError.message;
      if (serverError.error) return serverError.error;
      if (serverError.title) return serverError.title;

      // Handle validation errors
      if (serverError.errors) {
        return Object.values(serverError.errors).flat().join(", ");
      }
    }

    return error.message || "An unexpected error occurred";
  };

  return {
    leavePolicies,
    loading,
    fetchLeaveBalance,
    submitLeaveRequest,
    hasAvailableDays,
    calculateDays,
    getErrorMessage,
  };
}
